import { useCargas } from '../api/hooks';
import type { Carga, Cargas, EstadoCarga } from '../types/api';
import { duracion, fecha, haceCuanto } from '../utils/formato';

/** Etiqueta y clase de distintivo (las mismas de las tablas: badge-ok, badge-error…) de cada estado. */
const DISTINTIVO: Record<EstadoCarga, { etiqueta: string; clase: string }> = {
  ok: { etiqueta: 'Al día', clase: 'ok' },
  retrasada: { etiqueta: 'Con retraso', clase: 'retraso' },
  sin_datos: { etiqueta: 'Sin datos', clase: 'sin_datos' },
  error: { etiqueta: 'Error', clase: 'error' },
  fuera_de_horario: { etiqueta: 'Fuera de horario', clase: 'inactiva' },
  externa: { etiqueta: 'Externa', clase: 'pendiente' },
};

export function CargaBadge({ carga }: { carga: Carga }) {
  const d = DISTINTIVO[carga.estado] ?? { etiqueta: carga.estado, clase: 'sin_datos' };
  const titulo = carga.frescura_max_min ? `Frescura máxima: ${duracion(carga.frescura_max_min)}` : undefined;
  return (
    <span className={`badge badge-${d.clase}`} title={titulo}>
      {d.etiqueta}
    </span>
  );
}

export function AvisoFrescuraCargas({ cargas }: { cargas: Cargas | undefined }) {
  if (!cargas) return null;
  if (!cargas.recibido_at) {
    return (
      <div className="aviso aviso-error" role="alert">
        Todavía no ha llegado el catálogo de cargas: ¿está parado el DAG <code>dwh_salud</code>?
      </div>
    );
  }
  if (!cargas.fresco) {
    return (
      <div className="aviso aviso-error" role="alert">
        El último catálogo de cargas es de {haceCuanto(cargas.recibido_at)}: ¿está parado el DAG <code>dwh_salud</code>?
      </div>
    );
  }
  return null;
}

function fuenteDestino(c: Carga): string {
  if (!c.fuente && !c.destino) return '—';
  return `${c.fuente ?? '—'} → ${c.destino ?? '—'}`;
}

export function CargasPage() {
  const cargas = useCargas();

  if (cargas.isLoading) return <p className="cargando">Cargando el catálogo de cargas…</p>;
  if (cargas.error) return <div className="aviso aviso-error">{cargas.error.message}</div>;
  const d = cargas.data!;
  const ahora = new Date();
  const conProblemas = d.cargas.filter((c) => c.estado === 'error' || c.estado === 'retrasada').length;

  return (
    <>
      <div className="titulo">
        <h1>Cargas al DWH</h1>
      </div>
      <p className="subtitulo">
        {d.cargas.length} cargas
        {d.evaluado_at && <> · evaluado el {fecha(d.evaluado_at)}</>}
        {conProblemas > 0 && <span className="badge badge-error">{conProblemas} con error o retraso</span>}
      </p>
      <p className="ayuda">
        El catálogo de cargas vive en el repo <code>dwh</code> (<code>airflow/dags/functions/dwh_cargas.yml</code>) y la frescura de cada una la evalúa
        cada hora el DAG <code>dwh_salud</code> en Airflow, que la envía aquí.
      </p>
      <AvisoFrescuraCargas cargas={d} />
      <table className="tabla">
        <thead>
          <tr>
            <th>Carga</th>
            <th>Fuente → destino</th>
            <th>Código</th>
            <th>Programación</th>
            <th>Última correcta</th>
            <th>Estado</th>
            <th>Detalle</th>
          </tr>
        </thead>
        <tbody>
          {d.cargas.map((c) => (
            <tr key={c.id}>
              <td>
                {c.nombre ?? c.id}
                {c.nombre && (
                  <>
                    <br />
                    <code className="ayuda">{c.id}</code>
                  </>
                )}
              </td>
              <td>{fuenteDestino(c)}</td>
              <td>{c.codigo ? <code>{c.codigo}</code> : '—'}</td>
              <td>{c.programacion ?? '—'}</td>
              <td>
                {c.ultima_ok ? (
                  <>
                    {fecha(c.ultima_ok)}
                    <br />
                    <span className="ayuda">{haceCuanto(c.ultima_ok, ahora)}</span>
                  </>
                ) : (
                  '—'
                )}
              </td>
              <td>
                <CargaBadge carga={c} />
              </td>
              <td>{c.detalle ?? '—'}</td>
            </tr>
          ))}
          {d.cargas.length === 0 && (
            <tr>
              <td colSpan={7} className="vacio">
                No hay cargas en el catálogo.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </>
  );
}
